import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Form, Alert, Card } from 'react-bootstrap';
import Button from 'components/base/Button';
import { forgotPassword, ForgotPasswordPayload } from 'api/authApi';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const payload: ForgotPasswordPayload = { email: email.trim() };
      const response = await forgotPassword(payload);

      setSuccess(
        response.message ||
          'Password reset instructions have been sent to your email address.'
      );
      setEmail('');
    } catch (err) {
      console.error('Failed to send password reset email:', err);

      // Extract error message
      const axiosError = err as {
        response?: {
          data?: { detail?: string; message?: string };
        };
        message?: string;
      };

      const errorMessage =
        axiosError.response?.data?.detail ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Failed to send password reset email. Please try again.';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row min-vh-100 align-items-center justify-content-center">
        <div className="col-12 col-sm-10 col-md-8 col-lg-6 col-xl-5 col-xxl-4">
          <Card className="shadow-lg">
            <Card.Body className="p-4 p-sm-5">
              <div className="text-center mb-4">
                <h3 className="fw-bold">Forgot Password?</h3>
                <p className="text-muted">
                  Enter your email address and we'll send you instructions to
                  reset your password.
                </p>
              </div>

              {error && (
                <Alert variant="danger" className="mb-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert variant="success" className="mb-3">
                  <i className="fas fa-check-circle me-2"></i>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    disabled={loading || !!success}
                    autoComplete="email"
                  />
                </Form.Group>

                <div className="d-grid mb-3">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={loading || !!success}
                    className="py-2"
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Instructions'
                    )}
                  </Button>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-decoration-none"
                    onClick={e => {
                      if (loading) e.preventDefault();
                    }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Login
                  </Link>
                </div>
              </Form>

              {success && (
                <Alert variant="info" className="mt-3 mb-0">
                  <small>
                    <strong>Didn't receive the email?</strong>
                    <ul className="mb-0 mt-2">
                      <li>Check your spam/junk folder</li>
                      <li>Make sure you entered the correct email</li>
                      <li>Wait a few minutes and try again</li>
                    </ul>
                  </small>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
